import { Column, Index, JoinColumn, ManyToOne, OneToMany } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { CatalogValueEntity } from './catalog-value.entity';
import { ClientNoteEntity } from './client-note.entity';
import { ClientEntity } from './client.entity';
import { EventClientEntity } from './event-client.entity';
import { MSmClientOpportunityStageEntity } from './m-sm-client-opportunity-stage.entity';
import type { OpportunityActivityEntity } from './opportunity-activity.entity';
import { OpportunityStageHistoryEntity } from './opportunity-stage-history.entity';
import type { OpportunityUserEntity } from './opportunity-user.entity';
import { OrderEntity } from './order.entity';
import { UserEntity } from './user.entity';

@Index('idx_opportunity_name_fulltext', ['mSmName'], { fulltext: true })
@Index(
	'm_sm_client_opportunity_m_sm_fk_order__id__deleted_userId',
	['deletedUserId', 'mSmFkOrderId'],
	{},
)
@Index('m_sm_fk_client__id', ['mSmFkClientId'], {})
@Index(
	'm_sm_fk_m_sm_client_opportunity_stage__id',
	['mSmFkMSmClientOpportunityStageId'],
	{},
)
@Index('m_sm_fk_order__id', ['mSmFkOrderId'], {})
@Index('m_sm_client_opportunity__fk_project__id', ['fkProjectId'], {})
@Index('m_sm_client_opportunity__fk_job__id', ['fkJobId'], {})
@Index('m_sm_fk_user__id', ['mSmFkUserId'], {})
// PD-16477 — an import looks its earlier opportunities up by external id on
// every run.
@Index('idx_client_opportunity_external_id', ['externalId'], {})
@Entity('m_sm_client_opportunity', {}, { label: 'Client Opportunity' })
export class MSmClientOpportunityEntity extends BaseEntity {
	@Column('varchar', { name: 'm_sm_name', length: 255 })
	public mSmName!: string;

	@Column('int', {
		name: 'm_sm_fk_m_sm_client_opportunity_stage__id',
		unsigned: true,
	})
	public mSmFkMSmClientOpportunityStageId!: number;

	@Column('int', { name: 'm_sm_ordinal', unsigned: true, default: () => "'0'" })
	public mSmOrdinal!: number;

	@Column('int', { name: 'm_sm_fk_client__id', unsigned: true })
	public mSmFkClientId!: number;

	@Column('int', { name: 'm_sm_fk_order__id', nullable: true, unsigned: true })
	public mSmFkOrderId!: number | null;

	/**
	 * PD-17094 — the construction project this opportunity is a bid on
	 * (tenants with the jobs add-on). Nullable and SM2-only; SM1 never reads it.
	 */
	@Column('int', { name: 'fk_project__id', nullable: true, unsigned: true })
	public fkProjectId!: number | null;

	/**
	 * PD-17109 — the job this opportunity is a piece of (tenants with the jobs
	 * add-on). Many opportunities can sit on one job: a job can exist before
	 * we learn of work on it, and each piece we bid is its own opportunity.
	 * Always a job of the opportunity's own customer (OpportunityJobLink rules
	 * in JobsService). `job.fk_m_sm_client_opportunity__id` stays the single
	 * opportunity a job was created from.
	 *
	 * SM1 exposure: none. The legacy CRM shares this table, but the column is
	 * nullable with no default and SM1 never names it when it inserts or reads
	 * opportunities. No foreign key constraint is declared, so SM1's writes to
	 * this table take no new lock or check.
	 */
	@Column('int', { name: 'fk_job__id', nullable: true, unsigned: true })
	public fkJobId!: number | null;

	@Column('decimal', {
		name: 'm_sm_amount',
		nullable: true,
		precision: 15,
		scale: 2,
	})
	public mSmAmount!: string | null;

	@Column('datetime', { name: 'm_sm_closeDate', nullable: true })
	public mSmCloseDate!: Date | null;

	@Column('int', { name: 'm_sm_fk_user__id', unsigned: true })
	public mSmFkUserId!: number;

	@Column('json', {
		name: 'customFields',
		nullable: true,
		comment: 'JSON key-value pairs for tenant-specific custom fields.',
	})
	public customFields!: Record<string, unknown> | null;

	/**
	 * PD-16477 — the id of the record in the outside system an opportunity was
	 * imported from (for example a quote or deal in a CRM a tenant is moving
	 * off). A re-runnable import uses it to find the opportunity it already
	 * created instead of adding a second one. Null on every opportunity created
	 * in Workd.
	 *
	 * SM1 exposure: none. The legacy CRM does not name the column when it
	 * inserts or reads opportunities, and the column is nullable with no
	 * default.
	 */
	@Column('varchar', { name: 'externalId', nullable: true, length: 255 })
	public externalId!: string | null;

	/**
	 * PD-17101 — why a lost opportunity was lost: a value of the tenant's
	 * Opportunity Lost Reasons list (catalog_value). Required when the
	 * opportunity moves to a Lost stage; cleared when it moves out of one.
	 *
	 * SM1 exposure (this and the four columns below): none. They are nullable
	 * with no default, and the legacy CRM never names them when it inserts or
	 * reads opportunities.
	 */
	@Column('int', { name: 'fk_catalog_value__id_lostReason', nullable: true, unsigned: true })
	public lostReasonId!: number | null;

	@Column('varchar', { name: 'lostReasonNote', nullable: true, length: 1000 })
	public lostReasonNote!: string | null;

	/** PD-17101 — how far above the winning price ours was, in dollars (optional). */
	@Column('decimal', { name: 'priceGapAmount', nullable: true, precision: 15, scale: 2 })
	public priceGapAmount!: string | null;

	/** PD-17101 — the same as a percentage of the winning price (optional). */
	@Column('decimal', { name: 'priceGapPercent', nullable: true, precision: 7, scale: 2 })
	public priceGapPercent!: string | null;

	/** PD-17101 — when the opportunity last moved to a Won or Lost stage. */
	@Column('datetime', { name: 'outcomeDate', nullable: true })
	public outcomeDate!: Date | null;

	@ManyToOne(() => CatalogValueEntity, { onDelete: 'RESTRICT', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_catalog_value__id_lostReason', referencedColumnName: 'id' }])
	public lostReason!: CatalogValueEntity | null;

	@OneToMany(
		() => ClientNoteEntity,
		(clientNote) => clientNote.mSmFkMSmClientOpportunity,
		{},
	)
	public clientNotes!: Array<ClientNoteEntity>;

	@ManyToOne(
		() => MSmClientOpportunityStageEntity,
		(mSmClientOpportunityStage) =>
			mSmClientOpportunityStage.mSmClientOpportunities,
		{ onDelete: 'RESTRICT', onUpdate: 'RESTRICT' },
	)
	@JoinColumn([
		{
			name: 'm_sm_fk_m_sm_client_opportunity_stage__id',
			referencedColumnName: 'id',
		},
	])
	public mSmFkMSmClientOpportunityStage!: MSmClientOpportunityStageEntity;

	@ManyToOne(() => ClientEntity, (client) => client.clientOpportunities, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'm_sm_fk_client__id', referencedColumnName: 'id' }])
	public mSmFkClient!: ClientEntity;

	@ManyToOne(() => OrderEntity, (order) => order.mSmClientOpportunities, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'm_sm_fk_order__id', referencedColumnName: 'id' }])
	public mSmFkOrder!: OrderEntity;

	@ManyToOne(() => UserEntity, (user) => user.mSmClientOpportunities, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'm_sm_fk_user__id', referencedColumnName: 'id' }])
	public mSmFkUser!: UserEntity;

	@OneToMany(
		() => EventClientEntity,
		(eventClient) => eventClient.fkMSmClientOpportunity,
		{},
	)
	public eventClients!: Array<EventClientEntity>;

	@OneToMany(
		() => OpportunityStageHistoryEntity,
		(stageHistory) => stageHistory.fkOpportunity,
		{},
	)
	public stageHistories!: Array<OpportunityStageHistoryEntity>;

	@OneToMany(
		'OpportunityActivityEntity',
		(activity: OpportunityActivityEntity) => activity.fkOpportunity,
		{},
	)
	public activities!: Array<OpportunityActivityEntity>;

	@OneToMany(
		'OpportunityUserEntity',
		(user: OpportunityUserEntity) => user.fkOpportunity,
		{},
	)
	public opportunityUsers!: Array<OpportunityUserEntity>;
}
